import { PictureAsPdf } from "@mui/icons-material";
import { Avatar, Box, useMediaQuery, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import React, { useCallback, useEffect, useState } from "react";
import VideoThumbnail from "react-video-thumbnail";
import { getMimetype, Mimetype } from "src/utils/get-mimetype";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

interface IInstagramRenderImgPreviewProps {
  showThumbnail?: boolean;
  file: File | null;
  mimetype: Mimetype | undefined | string;
  already_uploaded_url: string | null;
  size?: number;
}

export const InstagramRenderImgPreview: React.FC<IInstagramRenderImgPreviewProps> = ({
  already_uploaded_url,
  file,
  mimetype,
  showThumbnail = true,
  size = 250,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const _mimetype = getMimetype(mimetype);

  useEffect(() => {
    if (file && file instanceof Blob && _mimetype === Mimetype.Video) {
      const fileUrl = URL.createObjectURL(file);
      setThumbnail(fileUrl);
      return () => URL.revokeObjectURL(fileUrl); // Clear URL when unmounting
    }
  }, [file, _mimetype]);

  const renderMedia = useCallback(() => {
    let imageUrl = already_uploaded_url || "";

    if (file instanceof Blob && file) {
      imageUrl = URL.createObjectURL(file);
    }

    const height = size;

    switch (_mimetype) {
      case Mimetype.Image:
        return (
          <div
            style={{ position: "relative", width: "100%", maxWidth: "95vw", overflow: "hidden" }}
          >
            <Zoom zoomMargin={0}>
              <img
                src={imageUrl}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "95vw",
                  maxHeight: "95vh",
                  objectFit: "contain",
                  cursor: "zoom-in",
                }}
              />
            </Zoom>
          </div>
        );
      case Mimetype.Video:
        return (
          <div
            style={{
              width: "100%",
              height,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.divider,
            }}
          >
            {showThumbnail ? (
              <VideoThumbnail
                videoUrl={thumbnail || imageUrl}
                thumbnailHandler={(thumbnail) => setThumbnail(thumbnail)}
                snapshotAtTime={10}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <video
                controls
                src={imageUrl}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            )}
          </div>
        );
      case Mimetype.Pdf: {
        if (!imageUrl) {
          return null;
        }

        const pdfViewerHeight = showThumbnail
          ? `${size}px`
          : isMobile
            ? "70vh"
            : `${Math.max(size, 600)}px`;

        return (
          <>
            {showThumbnail ? (
              <Avatar variant="square" style={{ width: "100%", height }}>
                <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
                  <PictureAsPdf fontSize="large" color="primary" style={{ fontSize: 75 }} />
                </Stack>
              </Avatar>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: pdfViewerHeight,
                  minHeight: pdfViewerHeight,
                  borderRadius: 1,
                  overflow: "hidden",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <object
                  data={imageUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  aria-label="Visualização do PDF"
                  style={{ width: "100%", height: "100%" }}
                >
                  <iframe
                    title="Visualização do PDF"
                    src={imageUrl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    scrolling="auto"
                  />
                </object>
              </Box>
            )}
          </>
        );
      }
      default:
        return <p>Erro no upload</p>;
    }
  }, [
    already_uploaded_url,
    file,
    size,
    _mimetype,
    theme.palette.divider,
    showThumbnail,
    thumbnail,
    isMobile,
  ]);

  return renderMedia();
};
